import {ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, Output, ChangeDetectionStrategy} from '@angular/core';
import {NgTemplateOutlet} from "@angular/common";
import {forkJoin, Observable, of, Subscription, timeout, timer} from "rxjs";
import {isDefined, unsubscribe} from "../utils/epic.utils";
import {Size} from "../../base/data/size";
import {PendingRequestService} from "../../services/request/pending-request.service";

@Component({
  selector: 'acc-button',
  imports: [
    NgTemplateOutlet
  ],
  templateUrl: './button.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './button.component.scss'
})
export class ButtonComponent implements OnDestroy {

  public static readonly DEFAULT_LOADING_TIMEOUT: number = 2000;
  public static readonly DEFAULT_RESPONSE_TIMEOUT: number = 60000;

  @Input()
  public id: string | undefined;

  @Input()
  public type: 'button' = 'button' as const;

  @Input()
  public status: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'danger'
    | 'outline-primary' | 'outline-secondary' | 'outline-info' | 'outline-success' | 'outline-warning'
    | 'outline-danger' | 'outline-gray' = 'primary';

  @Input()
  public size: 'lg' | 'sm' | 'xs' | Size | undefined;

  @Input()
  public minWidth: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 12 | 20 | 30 | 40 | undefined;

  @Input()
  public accClass: string | undefined;

  @Input()
  public disabled: boolean = false;

  // '<= 0' = disabled
  @Input()
  public loadingTimeout: number = ButtonComponent.DEFAULT_LOADING_TIMEOUT;

  // Longest wait for the answers to the requests the click started - afterwards the button unlocks without them.
  @Input()
  public responseTimeout: number = ButtonComponent.DEFAULT_RESPONSE_TIMEOUT;

  @Output()
  public readonly accClick: EventEmitter<any> = new EventEmitter<any>();

  protected loading: boolean = false;

  private readonly pendingRequests: PendingRequestService = inject(PendingRequestService);
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private loadingSub: Subscription | undefined;

  ngOnDestroy(): void {
    unsubscribe(this.loadingSub);
  }

  // markForCheck: the lock ends in a timer or an HTTP callback - under an OnPush parent the spinner would stay otherwise.
  public startLoading(): void {
    this.loading = true;
    this.changeDetector.markForCheck();
  }

  public stopLoading(): void {
    unsubscribe(this.loadingSub);
    this.loading = false;
    this.changeDetector.markForCheck();
  }

  protected onClick(event: MouseEvent): void {
    const answers: Observable<void> | undefined = this.pendingRequests.capture(() => this.accClick.emit(event));
    if (this.loadingTimeout > 0) {
      this.lockUntil(answers);
    }
  }

  protected getButtonClass(): string {
    const size: string | undefined = this.getSize();
    const sizeClass: string = isDefined(size) ? 'btn-' + size : '';
    const minWidth: string = isDefined(this.minWidth) ? 'acc-btn-mw-' + this.minWidth : '';
    const clazz: string = isDefined(this.accClass) ? this.accClass : '';
    return ('btn btn-' + this.status + ' ' + sizeClass + ' ' + minWidth + ' ' + clazz).trimEnd();
  }

  protected getSpinnerClass(): string {
    const size: string = this.size ? 'spinner-border-' + this.size : 'spinner-border-default';
    return 'spinner-border ' + size;
  }

  private getSize(): string | undefined {
    if (this.size instanceof Size) {
      return this.size.value;
    }
    return this.size;
  }

  // At least loadingTimeout, and until the click's requests are answered - a fixed lock ends while a slow request,
  // e.g. signing an invoice, is still running, and the second click sends it twice.
  private lockUntil(answers: Observable<void> | undefined): void {
    unsubscribe(this.loadingSub);
    this.startLoading();
    const limit: number = Math.max(this.loadingTimeout, this.responseTimeout);
    this.loadingSub = forkJoin([timer(this.loadingTimeout), answers ?? of(undefined)])
      .pipe(timeout({first: limit, with: () => of(undefined)}))
      .subscribe({complete: () => this.stopLoading()});
  }
}
